import type { Granularity } from "@seed-factory/core";
import { CalendarDays, SlidersHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { useFilters } from "../contexts/FiltersContext";
import {
  civilDateValue,
  granularityLabels,
  type PeriodPreset,
  periodLabels,
  rangeForCivilDates,
} from "../lib/dates";
import { useEstufaData } from "../lib/queries";

const presets = Object.keys(periodLabels) as PeriodPreset[];
const granularities: Granularity[] = ["day", "week", "month"];

export function FilterBar({ compact = false }: { compact?: boolean }) {
  const { filter, preset, setPreset, setGranularity, setSeedId, setPlatformId, setCustomRange } =
    useFilters();
  const { data } = useEstufaData();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => civilDateValue(filter.from));
  const [to, setTo] = useState(() => civilDateValue(filter.to));

  useEffect(() => {
    setFrom(civilDateValue(filter.from));
    setTo(civilDateValue(filter.to));
  }, [filter.from, filter.to]);

  return (
    <div className={`filter-wrap ${compact ? "filter-wrap--compact" : ""}`}>
      <button type="button" className="filter-trigger" onClick={() => setOpen((value) => !value)}>
        <SlidersHorizontal size={16} />
        <span>{periodLabels[preset]}</span>
      </button>
      {!compact ? (
        <span className="filter-summary">
          {filter.granularity === "day"
            ? "por dia"
            : filter.granularity === "week"
              ? "por semana"
              : "por mês"}
        </span>
      ) : null}
      {open ? (
        <div className="filter-popover">
          <div className="filter-section">
            <span className="filter-label">Período</span>
            <div className="chip-grid">
              {presets
                .filter((item) => item !== "custom")
                .map((item) => (
                  <button
                    type="button"
                    key={item}
                    className={`chip ${preset === item ? "chip--active" : ""}`}
                    onClick={() => setPreset(item)}
                  >
                    {periodLabels[item]}
                  </button>
                ))}
            </div>
          </div>
          <div className="filter-section">
            <span className="filter-label">Personalizado</span>
            <div className="date-row">
              <label htmlFor="filter-date-from">De</label>
              <input
                id="filter-date-from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
              <span>até</span>
              <label className="sr-only" htmlFor="filter-date-to">
                Até
              </label>
              <input
                id="filter-date-to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
              <button
                type="button"
                className="small-button"
                onClick={() => {
                  const range = rangeForCivilDates(from, to);
                  setCustomRange(range.from, range.to);
                }}
              >
                <CalendarDays size={15} />
                Aplicar
              </button>
            </div>
          </div>
          <div className="filter-section">
            <span className="filter-label">Granularidade</span>
            <div className="chip-grid">
              {granularities.map((item) => (
                <button
                  type="button"
                  key={item}
                  className={`chip ${filter.granularity === item ? "chip--active" : ""}`}
                  onClick={() => setGranularity(item)}
                >
                  {granularityLabels[item]}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-two-columns">
            <div className="filter-section">
              <label htmlFor="filter-seed">Seed</label>
              <select
                id="filter-seed"
                value={filter.seedId ?? ""}
                onChange={(event) => setSeedId(event.target.value || undefined)}
              >
                <option value="">Todas</option>
                {data?.seeds.map((seed) => (
                  <option key={seed.id} value={seed.id}>
                    {seed.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-section">
              <label htmlFor="filter-platform">Plataforma</label>
              <select
                id="filter-platform"
                value={filter.platformId ?? ""}
                onChange={(event) => setPlatformId(event.target.value || undefined)}
              >
                <option value="">Todas</option>
                {data?.platforms.map((platform) => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="button" className="popover-close" onClick={() => setOpen(false)}>
            Fechar filtros
          </button>
        </div>
      ) : null}
    </div>
  );
}
